import { useAuth } from "AuthProvider";
import { RoutePaths } from "enums/RoutePaths";
import useTvApi, { isAbort, messageOf } from "hooks/useTvApi";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Icon,
  IconButton,
  PageHeader,
  SegmentedControl,
  Select,
  Skeleton,
  Textarea,
  TextField,
  Toggle,
  styles as ui,
  useToast,
} from "stories/Admin";
import {
  LAYOUTS,
  layoutName,
  Poster,
  THEMES,
  themeName,
  TvPosterDesign,
  TvPosterLayout,
  TvPosterSaveRequest,
  TvPosterSuggestRequest,
  TvPosterSuggestResponse,
} from "types/TvPosterTypes";
import {
  AttachKind,
  AttachMenu,
  Attachments,
  ImageUpload,
} from "./ImageUpload";
import {
  BLOCK_KEYS,
  BLOCK_LABELS,
  BlockKey,
  BlockValues,
  canPreview,
  EditorState,
  emptyEditor,
  fromDesign,
  LAYOUT_BLOCKS,
  MAX,
  saveProblem,
  toDesign,
} from "./tvDesign";
import styles from "./TvStudioPage.module.scss";

/*
 * Lag plakat: standard er AI (fritekst + vedlegg → tre forslag). «Fyll ut
 * selv» er den fulle editoren. Begge bruker samme forhåndsvisning (JPEG fra
 * backend) og lagres som vanlige plakater.
 */

type Mode = "ai" | "manual";

interface Suggestion {
  design: TvPosterDesign;
  image: string | null;
  failed: boolean;
}

const REPEAT_OPTIONS = [
  ...[
    "mandag",
    "tirsdag",
    "onsdag",
    "torsdag",
    "fredag",
    "lørdag",
    "søndag",
  ].map((d) => `Hver ${d}`),
  "Annenhver fredag",
  "Annenhver lørdag",
  "Annenhver søndag",
  "Hverdager",
  "Hver helg",
  "Hver dag",
  "Hver dag i ramadan",
];

const PRIORITIES = Array.from({ length: 100 }, (_, i) => i + 1);
const NO_ATTACHMENTS: Attachments = {
  qrImageUrl: null,
  speakerPhotoUrl: null,
  imageUrl: null,
};

/** Små skisser av oppsettene. */
const LayoutSketch: React.FC<{ layout: TvPosterLayout }> = ({ layout }) => {
  const shapes: Record<TvPosterLayout, React.ReactNode> = {
    ANNOUNCEMENT: (
      <>
        <rect x="4" y="6" width="22" height="4" rx="2" />
        <rect x="4" y="13" width="34" height="7" rx="2" />
        <rect x="4" y="24" width="28" height="3" rx="1.5" />
        <rect x="4" y="30" width="24" height="3" rx="1.5" />
        <rect x="44" y="6" width="28" height="30" rx="4" />
      </>
    ),
    EVENT: (
      <>
        <rect x="4" y="6" width="17" height="30" rx="4" />
        <rect x="26" y="8" width="16" height="4" rx="2" />
        <rect x="26" y="15" width="26" height="7" rx="2" />
        <rect x="26" y="26" width="22" height="3" rx="1.5" />
        <rect x="58" y="15" width="14" height="14" rx="2" />
      </>
    ),
    PROGRAM: (
      <>
        <rect x="4" y="6" width="34" height="6" rx="2" />
        <rect x="4" y="16" width="7" height="3" rx="1.5" />
        <rect x="14" y="16" width="26" height="3" rx="1.5" />
        <rect x="4" y="23" width="7" height="3" rx="1.5" />
        <rect x="14" y="23" width="22" height="3" rx="1.5" />
        <rect x="4" y="30" width="7" height="3" rx="1.5" />
        <rect x="14" y="30" width="24" height="3" rx="1.5" />
        <rect x="54" y="13" width="18" height="18" rx="2" />
      </>
    ),
    FUNDRAISER: (
      <>
        <rect x="4" y="6" width="16" height="4" rx="2" />
        <rect x="4" y="13" width="32" height="7" rx="2" />
        <rect x="4" y="24" width="40" height="4" rx="2" opacity=".35" />
        <rect x="4" y="24" width="26" height="4" rx="2" />
        <rect x="4" y="31" width="22" height="5" rx="2" />
        <rect x="52" y="9" width="20" height="26" rx="3" />
      </>
    ),
    IMAGE: (
      <>
        <rect x="4" y="5" width="68" height="34" rx="4" opacity=".25" />
        <path d="M10 34l14-13 10 9 8-6 14 10z" />
        <circle cx="56" cy="14" r="4" />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 76 44" aria-hidden="true" fill="currentColor">
      {shapes[layout]}
    </svg>
  );
};

const TvStudioPage: React.FC = () => {
  const { selectedOrgNumber: orgNr } = useAuth();
  const api = useTvApi();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const idParam = searchParams.get("id");

  const [mode, setMode] = useState<Mode>(idParam ? "manual" : "ai");
  const [editor, setEditor] = useState<EditorState>(emptyEditor);
  const [existing, setExisting] = useState<Poster | null>(null);
  const [showOnTv, setShowOnTv] = useState(true);
  const [priority, setPriority] = useState(5);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // AI
  const [prompt, setPrompt] = useState("");
  const [attachments, setAttachments] = useState<Attachments>(NO_ATTACHMENTS);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [chosen, setChosen] = useState<number | null>(null);
  /** AI-loggen forslagene kom fra; sendes som `aiLogId` når plakaten lagres. */
  const [logId, setLogId] = useState<number | null>(null);
  const suggestionUrls = useRef<string[]>([]);

  // Forhåndsvisning
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** Liggende 16:9 eller stående 4:5 (`?format=portrait`). */
  const [previewFormat, setPreviewFormat] = useState<"landscape" | "portrait">(
    "landscape",
  );
  const previewUrlRef = useRef<string | null>(null);

  const design = useMemo(() => toDesign(editor), [editor]);
  const designKey = JSON.stringify(design);
  const base = `/api/v1/mosques/${orgNr}/tv-posters`;

  /** Det finnes et design å vise og lagre (i AI-modus: et valgt forslag). */
  const hasDesign = mode === "manual" || chosen !== null || !!existing;

  const reset = useCallback(() => {
    setEditor(emptyEditor());
    setExisting(null);
    setShowOnTv(true);
    setPriority(5);
    setChosen(null);
    setSaveError(null);
    setLoadError(null);
  }, []);

  // Rydd opp bilde-url-er når siden lukkes.
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      suggestionUrls.current.forEach((u) => URL.revokeObjectURL(u));
    },
    [],
  );

  // Last en eksisterende plakat (?id=123).
  const existingId = existing?.id;
  useEffect(() => {
    if (!orgNr) return;
    if (!idParam) {
      if (existingId !== undefined) reset();
      setLoadError(null);
      return;
    }
    if (String(existingId) === idParam) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api<Poster[]>(`/api/v1/poster/org/${orgNr}`, "GET")
      .then((list) => {
        if (cancelled) return;
        setLoading(false);
        const poster = (Array.isArray(list) ? list : []).find(
          (p) => String(p.id) === idParam,
        );
        if (!poster) {
          setLoadError("Fant ikke plakaten. Den kan være slettet.");
        } else if (!poster.design) {
          setLoadError(
            "Denne plakaten er lastet opp som bilde. Endre den under «Plakater på TV».",
          );
        } else {
          setEditor(fromDesign(poster.design));
          setExisting(poster);
          setShowOnTv(poster.activatedForTv);
          setPriority(poster.priority || 5);
          setMode("manual");
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setLoading(false);
        setLoadError(messageOf(e));
      });
    return () => {
      cancelled = true;
    };
  }, [idParam, orgNr, api, existingId, reset]);

  // Forhåndsvisning fra backend, ~400 ms etter siste endring.
  useEffect(() => {
    const current: TvPosterDesign = JSON.parse(designKey);
    if (!orgNr || !hasDesign || !canPreview(current)) {
      setPreviewLoading(false);
      setPreviewError(null);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const blob = await api<Blob>(
          `/api/v1/mosques/${orgNr}/tv-posters/preview${
            previewFormat === "portrait" ? "?format=portrait" : ""
          }`,
          "POST",
          current,
          { blob: true, signal: controller.signal },
        );
        const url = URL.createObjectURL(blob);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreviewUrl(url);
        setPreviewError(null);
        setPreviewLoading(false);
      } catch (e) {
        if (isAbort(e)) return;
        setPreviewError(messageOf(e));
        setPreviewLoading(false);
      }
    }, 400);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [designKey, orgNr, api, hasDesign, previewFormat]);

  const upload = useCallback(
    async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await api<{ url: string }>(
        `/api/v1/mosques/${orgNr}/tv-posters/assets`,
        "POST",
        form,
      );
      if (!res?.url) throw new Error("Opplastingen feilet. Prøv igjen.");
      return res.url;
    },
    [api, orgNr],
  );

  // ---------- Endringer i editoren ----------
  const set = <K extends keyof EditorState>(key: K, value: EditorState[K]) =>
    setEditor((e) => ({ ...e, [key]: value }));

  const setValue = <K extends BlockKey>(key: K, value: BlockValues[K]) =>
    setEditor((e) => ({ ...e, values: { ...e.values, [key]: value } }));

  const patchValue = <
    K extends "date" | "speaker" | "qr" | "vipps" | "goal" | "contact",
  >(
    key: K,
    patch: Partial<BlockValues[K]>,
  ) =>
    setEditor((e) => ({
      ...e,
      values: { ...e.values, [key]: { ...e.values[key], ...patch } },
    }));

  const setOn = (key: BlockKey, on: boolean) =>
    setEditor((e) => ({ ...e, on: { ...e.on, [key]: on } }));

  // ---------- AI ----------
  const promptTooLong = prompt.length > MAX.prompt;

  const suggest = async () => {
    if (!prompt.trim() || promptTooLong) return;
    setSuggesting(true);
    setSuggestError(null);
    const body: TvPosterSuggestRequest = {
      prompt: prompt.trim(),
      ...attachments,
    };
    try {
      const res = await api<TvPosterSuggestResponse>(
        `${base}/suggest`,
        "POST",
        body,
      );
      const list = Array.isArray(res?.suggestions) ? res.suggestions : [];
      setLogId(typeof res?.logId === "number" ? res.logId : null);
      suggestionUrls.current.forEach((u) => URL.revokeObjectURL(u));
      suggestionUrls.current = [];
      setChosen(null);
      setSuggestions(
        list.map((d) => ({ design: d, image: null, failed: false })),
      );
      if (list.length === 0)
        setSuggestError("AI ga ingen forslag. Prøv å skrive litt mer.");
      list.forEach(async (d, i) => {
        const update = (patch: Partial<Suggestion>) =>
          setSuggestions((prev) =>
            prev.map((s, j) =>
              j === i && s.design === d ? { ...s, ...patch } : s,
            ),
          );
        try {
          const blob = await api<Blob>(`${base}/preview`, "POST", d, {
            blob: true,
          });
          const url = URL.createObjectURL(blob);
          suggestionUrls.current.push(url);
          update({ image: url });
        } catch {
          update({ failed: true });
        }
      });
    } catch (e) {
      setSuggestError(messageOf(e));
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = (index: number) => {
    setEditor((prev) => fromDesign(suggestions[index].design, prev));
    setChosen(index);
    setSaveError(null);
  };

  const setAttachment = (kind: AttachKind, url: string | null) =>
    setAttachments((a) => ({ ...a, [kind]: url }));

  // ---------- Lagre ----------
  const save = async () => {
    const problem = saveProblem(design);
    if (problem) {
      setSaveError(problem);
      return;
    }
    setSaving(true);
    setSaveError(null);
    const body: TvPosterSaveRequest = {
      design,
      activatedForTv: showOnTv,
      activated: existing?.activated ?? false,
      priority,
      // Ny plakat fra et AI-forslag («Bruk dette», ev. justert).
      ...(!existing && chosen !== null && logId !== null
        ? { aiLogId: logId }
        : {}),
    };
    try {
      const saved = await api<Poster>(
        existing ? `${base}/${existing.id}` : base,
        existing ? "PUT" : "POST",
        body,
      );
      toast.success(existing ? "Endringene er lagret." : "Plakaten er lagret.");
      if (saved?.id !== undefined) {
        setExisting(saved);
        setSearchParams({ id: String(saved.id) }, { replace: true });
      }
    } catch (e) {
      setSaveError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const newPoster = () => {
    reset();
    setMode("ai");
    setSearchParams({});
  };

  // ---------- Visning ----------
  const used = LAYOUT_BLOCKS[editor.layout];
  const isImage = editor.layout === "IMAGE";

  const blockBody = (key: BlockKey): React.ReactNode => {
    const v = editor.values;
    switch (key) {
      case "date":
        return (
          <div className={styles.grid2}>
            <TextField
              label="Gjentas"
              placeholder="Én gang"
              help="F.eks. hver søndag. Datoen er da første gang, eller tom."
              list="tv-repeat-options"
              maxLength={MAX.repeat}
              value={v.date.repeat}
              onChange={(e) => patchValue("date", { repeat: e.target.value })}
            />
            <datalist id="tv-repeat-options">
              {REPEAT_OPTIONS.map((o) => (
                <option key={o} value={o} />
              ))}
            </datalist>
            <TextField
              label={v.date.repeat.trim() ? "Første gang (valgfritt)" : "Dato"}
              type="date"
              value={v.date.date}
              onChange={(e) => patchValue("date", { date: e.target.value })}
            />
            <TextField
              label="Fra"
              type="time"
              value={v.date.from}
              onChange={(e) => patchValue("date", { from: e.target.value })}
            />
            <TextField
              label="Til"
              type="time"
              value={v.date.to}
              onChange={(e) => patchValue("date", { to: e.target.value })}
            />
            <TextField
              label="Tillegg"
              placeholder="F.eks. etter isha"
              value={v.date.note}
              onChange={(e) => patchValue("date", { note: e.target.value })}
            />
          </div>
        );
      case "place":
        return (
          <TextField
            label="Sted"
            placeholder="Hovedsalen, 2. etasje"
            value={v.place}
            onChange={(e) => setValue("place", e.target.value)}
          />
        );
      case "speaker":
        return (
          <>
            <div className={styles.grid2}>
              <TextField
                label="Navn"
                value={v.speaker.name}
                onChange={(e) =>
                  patchValue("speaker", { name: e.target.value })
                }
              />
              <TextField
                label="Rolle"
                placeholder="Imam, sheikh …"
                value={v.speaker.role}
                onChange={(e) =>
                  patchValue("speaker", { role: e.target.value })
                }
              />
            </div>
            <ImageUpload
              label="Bilde av foredragsholder"
              value={v.speaker.photoUrl}
              onChange={(url) => patchValue("speaker", { photoUrl: url })}
              upload={upload}
            />
          </>
        );
      case "program":
        return (
          <>
            {v.program.map((row, i) => (
              <div key={i} className={styles.programRow}>
                <TextField
                  aria-label="Tid"
                  placeholder="10:00"
                  value={row.time}
                  onChange={(e) =>
                    setValue(
                      "program",
                      v.program.map((r, j) =>
                        j === i ? { ...r, time: e.target.value } : r,
                      ),
                    )
                  }
                />
                <TextField
                  aria-label="Punkt"
                  placeholder="Hva skjer"
                  value={row.text}
                  onChange={(e) =>
                    setValue(
                      "program",
                      v.program.map((r, j) =>
                        j === i ? { ...r, text: e.target.value } : r,
                      ),
                    )
                  }
                />
                <IconButton
                  label="Fjern rad"
                  icon={<Icon name="x" size={16} strokeWidth={2} />}
                  disabled={v.program.length === 1}
                  onClick={() =>
                    setValue(
                      "program",
                      v.program.filter((_, j) => j !== i),
                    )
                  }
                />
              </div>
            ))}
            <span>
              <Button
                size="sm"
                variant="secondary"
                icon={<Icon name="plus" size={14} strokeWidth={2} />}
                onClick={() =>
                  setValue("program", [...v.program, { time: "", text: "" }])
                }
              >
                Legg til rad
              </Button>
            </span>
          </>
        );
      case "qr":
        return (
          <>
            <SegmentedControl<"link" | "image">
              label="QR-kode"
              value={editor.qrMode}
              onChange={(m) => set("qrMode", m)}
              options={[
                { value: "link", label: "Lenke" },
                { value: "image", label: "Last opp QR-kode" },
              ]}
            />
            {editor.qrMode === "link" ? (
              <TextField
                label="Lenke"
                type="url"
                placeholder="https://"
                value={v.qr.url}
                onChange={(e) => patchValue("qr", { url: e.target.value })}
                help="QR-koden lages av lenken."
              />
            ) : (
              <ImageUpload
                label="QR-kode som bilde"
                value={v.qr.imageUrl}
                onChange={(url) => patchValue("qr", { imageUrl: url })}
                upload={upload}
              />
            )}
            <TextField
              label="Tekst under koden"
              placeholder="Meld deg på"
              value={v.qr.label}
              onChange={(e) => patchValue("qr", { label: e.target.value })}
            />
          </>
        );
      case "imageUrl":
        return (
          <ImageUpload
            label="Bilde"
            value={v.imageUrl}
            onChange={(url) => setValue("imageUrl", url)}
            upload={upload}
          />
        );
      case "vipps":
        return (
          <div className={styles.grid2}>
            <TextField
              label="Vipps-nummer"
              inputMode="numeric"
              value={v.vipps.number}
              onChange={(e) => patchValue("vipps", { number: e.target.value })}
            />
            <TextField
              label="Kontonummer"
              value={v.vipps.account}
              onChange={(e) => patchValue("vipps", { account: e.target.value })}
            />
          </div>
        );
      case "goal":
        return (
          <>
            <TextField
              label="Prosjekt"
              placeholder="Nytt tak"
              value={v.goal.project}
              onChange={(e) => patchValue("goal", { project: e.target.value })}
            />
            <div className={styles.grid2}>
              <TextField
                label="Mål (kr)"
                inputMode="numeric"
                value={v.goal.target}
                onChange={(e) =>
                  patchValue("goal", {
                    target: e.target.value.replace(/[^\d ]/g, ""),
                  })
                }
              />
              <TextField
                label="Samlet inn så langt (kr)"
                inputMode="numeric"
                value={v.goal.raised}
                onChange={(e) =>
                  patchValue("goal", {
                    raised: e.target.value.replace(/[^\d ]/g, ""),
                  })
                }
              />
            </div>
          </>
        );
      case "signup":
        return (
          <TextField
            label="Tekst"
            placeholder="Gratis · påmelding via QR"
            value={v.signup}
            onChange={(e) => setValue("signup", e.target.value)}
          />
        );
      case "contact":
        return (
          <div className={styles.grid2}>
            <TextField
              label="Navn"
              value={v.contact.name}
              onChange={(e) => patchValue("contact", { name: e.target.value })}
            />
            <TextField
              label="Telefon"
              type="tel"
              value={v.contact.phone}
              onChange={(e) => patchValue("contact", { phone: e.target.value })}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const aiPane = (
    <>
      <Card
        title="Beskriv plakaten"
        subtitle="Skriv hva plakaten skal handle om. AI skriver tittel og tekst, fyller inn dato, sted og foredragsholder, og foreslår oppsett og farger."
      >
        <div className={styles.stack}>
          <Textarea
            label="Hva skal plakaten handle om?"
            rows={4}
            maxChars={MAX.prompt}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="F.eks. Foredrag om tålmodighet fredag 23. oktober etter isha med imam Yusuf Rahman. Gratis, kaffe og te. QR til påmelding."
            error={
              promptTooLong
                ? `Teksten kan ha maks ${MAX.prompt} tegn.`
                : undefined
            }
            disabled={suggesting}
          />
          <AttachMenu
            value={attachments}
            onChange={setAttachment}
            upload={upload}
            disabled={suggesting}
          />
          <div className={styles.row}>
            <Button
              onClick={suggest}
              loading={suggesting}
              disabled={!orgNr || !prompt.trim() || promptTooLong}
              icon={<Icon name="spark" size={16} strokeWidth={2} />}
            >
              {suggesting ? "Lager forslag …" : "Lag tre forslag"}
            </Button>
            {suggesting && (
              <span className={styles.muted}>Dette tar 5–20 sekunder.</span>
            )}
          </div>
          {suggestError && <Alert tone="error">{suggestError}</Alert>}
        </div>
      </Card>

      {(suggesting || suggestions.length > 0) && (
        <Card
          title="Forslag"
          subtitle="Velg forslaget du liker best. Du kan bytte farger og justere etterpå."
        >
          <div className={styles.suggestions}>
            {suggesting
              ? [0, 1, 2].map((i) => (
                  <div key={i} className={styles.suggestion}>
                    <span className={styles.thumb}>
                      <Skeleton height="100%" radius={0} />
                    </span>
                    <Skeleton height={14} width="70%" />
                  </div>
                ))
              : suggestions.map((s, i) => (
                  <div
                    key={i}
                    className={`${styles.suggestion} ${
                      chosen === i ? styles.suggestionOn : ""
                    }`}
                  >
                    <span className={styles.thumb}>
                      {s.image ? (
                        <img
                          src={s.image}
                          alt={`Forslag ${i + 1}: ${s.design.title}`}
                        />
                      ) : s.failed ? (
                        <span className={styles.thumbNote}>
                          Fikk ikke vist forslaget
                        </span>
                      ) : (
                        <Skeleton height="100%" radius={0} />
                      )}
                    </span>
                    <b className={styles.suggestionTitle}>{s.design.title}</b>
                    <span className={styles.muted}>
                      {layoutName(s.design.layout)} ·{" "}
                      {themeName(s.design.theme)}
                    </span>
                    <Button
                      size="sm"
                      variant={chosen === i ? "mint" : "secondary"}
                      onClick={() => applySuggestion(i)}
                      icon={
                        chosen === i ? (
                          <Icon name="check" size={14} strokeWidth={2.4} />
                        ) : undefined
                      }
                    >
                      {chosen === i ? "Valgt" : "Bruk dette"}
                    </Button>
                  </div>
                ))}
          </div>
        </Card>
      )}
    </>
  );

  const manualPane = (
    <>
      <Card
        title="Mal"
        subtitle="Hvert oppsett plasserer informasjonen på sin faste måte."
      >
        <div className={styles.layouts} role="radiogroup" aria-label="Mal">
          {LAYOUTS.map((l) => (
            <button
              key={l.value}
              type="button"
              role="radio"
              aria-checked={editor.layout === l.value}
              className={`${styles.layout} ${
                editor.layout === l.value ? styles.layoutOn : ""
              }`}
              onClick={() => set("layout", l.value)}
            >
              <LayoutSketch layout={l.value} />
              <b>{l.name}</b>
              <small>{l.description}</small>
            </button>
          ))}
        </div>
      </Card>

      <Card title="Tekst">
        <div className={styles.stack}>
          {!isImage && (
            <TextField
              label="Merkelapp"
              placeholder="Foredrag, Innsamling …"
              maxLength={MAX.eyebrow}
              value={editor.eyebrow}
              onChange={(e) => set("eyebrow", e.target.value)}
            />
          )}
          <TextField
            label="Tittel"
            required
            maxLength={MAX.title}
            value={editor.title}
            onChange={(e) => set("title", e.target.value)}
            help={
              isImage
                ? "Vises i listen over plakater og i appen."
                : `Maks ${MAX.title} tegn, så den kan leses fra bakerst i salen. ${editor.title.length} / ${MAX.title}`
            }
            error={
              editor.title.length > MAX.title
                ? `Tittelen kan ha maks ${MAX.title} tegn.`
                : undefined
            }
          />
          {!isImage && (
            <>
              <TextField
                label="Undertittel"
                maxLength={MAX.subtitle}
                value={editor.subtitle}
                onChange={(e) => set("subtitle", e.target.value)}
              />
              <Textarea
                label="Tekst"
                rows={4}
                maxChars={MAX.body}
                value={editor.body}
                onChange={(e) => set("body", e.target.value)}
                error={
                  editor.body.length > MAX.body
                    ? `Teksten kan ha maks ${MAX.body} tegn.`
                    : undefined
                }
              />
              <TextField
                label="Arabisk tekst (valgfritt)"
                dir="rtl"
                lang="ar"
                className={ui.arabic}
                placeholder="الجمعة مباركة"
                value={editor.arabic}
                onChange={(e) => set("arabic", e.target.value)}
              />
            </>
          )}
        </div>
      </Card>

      {isImage ? (
        <Card title="Egen ferdig plakat">
          <ImageUpload
            label="Plakat"
            help="Last opp en plakat dere har laget selv, helst 1920 × 1080 px. Den vises i hele skjermen."
            value={editor.ownImageUrl}
            onChange={(url) => set("ownImageUrl", url)}
            upload={upload}
          />
        </Card>
      ) : (
        <Card
          title="Informasjon som vises"
          subtitle="Slå på det som skal med. Det du skriver huskes selv om du slår det av."
        >
          <div className={styles.blocks}>
            {BLOCK_KEYS.map((key) => {
              const on = editor.on[key];
              const isUsed = used.includes(key);
              return (
                <div
                  key={key}
                  className={`${styles.block} ${on ? styles.blockOn : ""} ${
                    isUsed ? "" : styles.blockUnused
                  }`}
                >
                  <div className={styles.blockHead}>
                    <Toggle
                      label={<b>{BLOCK_LABELS[key]}</b>}
                      checked={on}
                      onChange={(e) => setOn(key, e.target.checked)}
                    />
                    {!isUsed && (
                      <Badge tone="grey">Vises ikke i dette oppsettet</Badge>
                    )}
                  </div>
                  {on && (
                    <div className={styles.blockBody}>{blockBody(key)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </>
  );

  const previewCard = (
    <Card
      title="Forhåndsvisning"
      actions={
        <SegmentedControl<"landscape" | "portrait">
          label="Format"
          value={previewFormat}
          onChange={setPreviewFormat}
          options={[
            { value: "landscape", label: "Liggende" },
            { value: "portrait", label: "Stående" },
          ]}
        />
      }
    >
      <div className={styles.stack}>
        <span className={styles.formatNote}>
          Plakaten lages automatisk både liggende og stående. TV-en velger den
          som passer best til plassen.
        </span>
        <div
          className={`${styles.tv} ${
            previewFormat === "portrait" ? styles.tvPortrait : ""
          }`}
          aria-busy={previewLoading || undefined}
        >
          {hasDesign && previewUrl && canPreview(design) ? (
            <img src={previewUrl} alt={`Plakat: ${design.title}`} />
          ) : (
            <span className={styles.tvNote}>
              {!hasDesign
                ? "Lag forslag med AI og velg ett, så vises plakaten her."
                : isImage
                  ? "Last opp plakaten for å se den her."
                  : "Skriv en tittel for å se plakaten."}
            </span>
          )}
          {previewLoading && (
            <span className={styles.tvLoading}>
              <span className={ui.spinner} aria-hidden="true" />
              Oppdaterer …
            </span>
          )}
        </div>
        {previewError && <Alert tone="error">{previewError}</Alert>}

        {hasDesign && (
          <>
            <div>
              <div className={styles.eyebrow}>Fargemal</div>
              <div
                className={styles.themes}
                role="radiogroup"
                aria-label="Fargemal"
              >
                {THEMES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    role="radio"
                    aria-checked={editor.theme === t.value}
                    className={`${styles.theme} ${
                      editor.theme === t.value ? styles.themeOn : ""
                    }`}
                    onClick={() => set("theme", t.value)}
                  >
                    <i style={{ background: t.background }}>
                      <i style={{ background: t.accent }} />
                    </i>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            {mode === "ai" && (
              <div className={styles.row}>
                <span className={styles.muted}>
                  {layoutName(editor.layout)}. Vil du endre tekst eller
                  informasjon?
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Icon name="edit" size={14} strokeWidth={2} />}
                  onClick={() => setMode("manual")}
                >
                  Juster selv
                </Button>
              </div>
            )}

            <div className={styles.saveRow}>
              <Toggle
                label={<b>Vis på TV</b>}
                checked={showOnTv}
                onChange={(e) => setShowOnTv(e.target.checked)}
              />
              <Select
                label="Prioritet"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value, 10))}
                fieldClassName={styles.priority}
                help="Lavere tall vises først."
              >
                {(PRIORITIES.includes(priority)
                  ? PRIORITIES
                  : [priority, ...PRIORITIES]
                ).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            </div>
            {saveError && <Alert tone="error">{saveError}</Alert>}
            <div className={styles.row}>
              <Button
                onClick={save}
                loading={saving}
                disabled={!orgNr}
                icon={<Icon name="check" size={16} strokeWidth={2} />}
              >
                {existing ? "Lagre endringer" : "Lagre plakat"}
              </Button>
              {existing && (
                <Button
                  variant="ghost"
                  onClick={() => navigate(RoutePaths.Posters)}
                >
                  Til plakatene
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );

  return (
    <>
      <PageHeader
        title="Lag plakat"
        crumb={existing ? `Endrer «${existing.tittel}»` : undefined}
        subtitle="Lag en plakat til TV-skjermen med AI, eller fyll ut en mal selv."
        actions={
          (existing || loadError) && (
            <Button
              variant="secondary"
              icon={<Icon name="plus" size={16} strokeWidth={2} />}
              onClick={newPoster}
            >
              Ny plakat
            </Button>
          )
        }
      />
      <SectionTabs />

      {loadError ? (
        <Alert tone="error">{loadError}</Alert>
      ) : loading ? (
        <div className={styles.studio}>
          <Skeleton height={420} radius={20} />
          <Skeleton height={320} radius={20} />
        </div>
      ) : (
        <>
          <SegmentedControl<Mode>
            label="Slik lager du plakaten"
            className={styles.modeSwitch}
            value={mode}
            onChange={setMode}
            options={[
              { value: "ai", label: "Med AI" },
              { value: "manual", label: "Fyll ut selv" },
            ]}
          />
          <div className={styles.studio}>
            <div className={styles.column}>
              {mode === "ai" ? aiPane : manualPane}
            </div>
            <div className={styles.column}>{previewCard}</div>
          </div>
        </>
      )}
    </>
  );
};

export default TvStudioPage;
